<script lang="ts" setup>
import PortfolioBodyElement from './components/PortfolioBodyElement.vue';
import PortfolioBodyElementAssets from './components/PortfolioBodyElementAssets.vue';
import PortfolioBodyPlatform from './components/PortfolioBodyPlatform.vue';
import { walletTokensPlatformId } from '@/constants';
import { priceFormat } from '@/helpers/priceFormat';
import usePortfolio from '../usePortfolio';
import { computed, inject } from 'vue';
import { NetworkIdType } from '@sonarwatch/portfolio-core';
import Panel from './components/Panel.vue';
import Airdrop from './components/Airdrop';

const {
  excludedChains,
  portfolioInfo,
  isLoading,
  isInitialized,
  addressesInfo,
  walletInfo,
  computeValue,
  hiddenElementsValue,
} = inject('portfolioData') as ReturnType<typeof usePortfolio>;

const sectionMustBeShown = (networkIds: Set<NetworkIdType>): boolean => {
  if (excludedChains.value.length === 0) return true;
  const d = Array.from(networkIds);
  return d.some((x) => !excludedChains.value.includes(x));
};

const shownElements = computed(() => {
  return portfolioInfo.elementsByPlatformId.value.filter((e) =>
    sectionMustBeShown(e.networkIds),
  );
});

const noData = computed(() => {
  return (
    isInitialized.value &&
    !isLoading.value &&
    walletInfo.value.assets.length === 0 &&
    shownElements.value.length === 0
  );
});
</script>

<template>
  <!-- No data   -->
  <v-card class="text-center" v-if="noData">
    <v-card-text class="text-h6">No asset found </v-card-text>
  </v-card>

  <div v-else class="flex flex-col gap-4">
    <Panel>
      <template #header>
        <PortfolioBodyPlatform
          :platform-id="walletTokensPlatformId"
          :value="walletInfo.value"
        />
      </template>
      <template #content>
        <PortfolioBodyElementAssets
          label="Wallet"
          :assets="walletInfo.assets"
          :show-owner="addressesInfo.length !== 1"
        />
      </template>
    </Panel>

    <div v-for="eByPlatform in shownElements" :key="eByPlatform.platformId">
      <Panel>
        <template #header>
          <PortfolioBodyPlatform
            :platform-id="eByPlatform.platformId"
            :value="computeValue(eByPlatform.elements, excludedChains)"
          />
        </template>
        <template #content>
          <PortfolioBodyElement
            v-for="(element, i) in eByPlatform.elements"
            :key="`${eByPlatform.platformId}-${i}`"
            :element="element"
            :show-owner="addressesInfo.length !== 1"
            :class="{ 'mb-4': i < eByPlatform.elements.length - 1 }"
          />
        </template>
      </Panel>
    </div>

    <div v-if="hiddenElementsValue > 0" class="text-caption">
      <v-icon icon="mdi-eye-off-outline" size="14" class="me-2" />Hidden value :
      {{ priceFormat(hiddenElementsValue) }}
    </div>
  </div>
</template>
